/**
 * A QR code for the next device — one driver's, or the firm's free one — and the wait for it
 * to be scanned.
 *
 * Issued when shown, not before: the code is a key to the driver's account for its quarter of
 * an hour, so it exists only while somebody is looking at it. The screen asks every two
 * seconds whether a phone has scanned it, and says so the moment one has — the administrator
 * is standing with the phone in their other hand.
 */

import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../settings/errors';
import { Banner, Button } from '../ui';
import { devicesApi, type DeviceTicket } from './api';

/** What the code is for: a driver, or the firm's list with the number the office gave it. */
export type QrFor = { driverId: string } | { label: string | null };

const LOOK_EVERY_MS = 2000;

export function QrTicket({ target, onScanned }: { target: QrFor; onScanned: () => void }) {
  const { t } = useTranslation();

  const [ticket, setTicket] = useState<DeviceTicket | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [scanned, setScanned] = useState(false);
  const [lapsed, setLapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const issued =
          'driverId' in target ? await devicesApi.ticket(target.driverId) : await devicesApi.freeTicket(target.label);
        const drawn = await QRCode.toDataURL(issued.payload, { margin: 1, width: 260 });

        if (cancelled) return;

        setTicket(issued);
        setImage(drawn);
        setLapsed(false);
        setError(null);
      } catch (failure) {
        if (!cancelled) setError(describe(failure, t, 'devices.qr.couldNotIssue'));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per attempt: a new object for the target on every render must not mint a new code under a phone already scanning the old one
  }, [attempt, t]);

  useEffect(() => {
    if (ticket === null || scanned || lapsed) return;

    const timer = setInterval(() => {
      void (async () => {
        try {
          const state = await devicesApi.ticketState(ticket.ticketId);

          if (state.claimed) {
            setScanned(true);
            onScanned();
          } else if (Date.parse(state.expiresAt) <= Date.now()) {
            setLapsed(true);
          }
        } catch {
          // A missed look is not worth a word; the next one is two seconds away.
        }
      })();
    }, LOOK_EVERY_MS);

    return () => clearInterval(timer);
  }, [ticket, scanned, lapsed, onScanned]);

  if (error !== null) return <Banner tone="danger">{error}</Banner>;

  if (scanned) {
    const forDriver = 'driverId' in target;

    return <Banner tone="ok">{t(forDriver ? 'devices.qr.scanned' : 'devices.qr.registered')}</Banner>;
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {lapsed ? (
        <Banner
          tone="warn"
          actions={
            <Button size="sm" onClick={() => setAttempt((n) => n + 1)}>
              {t('devices.qr.again')}
            </Button>
          }
        >
          {t('devices.qr.lapsed')}
        </Banner>
      ) : image !== null ? (
        <img src={image} alt={t('devices.qr.alt')} width={260} height={260} />
      ) : (
        <p className="m-0 text-secondary text-ink-3">{t('app.loading')}</p>
      )}
      <p className="m-0 text-secondary text-ink-3 text-center">{t('devices.qr.help')}</p>
    </div>
  );
}
